import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useErrorToast } from './queryUtils';
import {
  getBotStatus,
  getTimeLoggingStatus,
  getNonceValidity,
  connectBot,
  disconnectBot,
  rotateClaudeToken,
  selfConnectBot,
  updateBotModel,
  type ConnectInput,
  type RotateInput,
} from '../services/api/botConnection';

const BOT_CONNECTION_KEY = ['bot-connection'];
const PERSONAL_API_TOKENS_KEY = ['personal-api-tokens'];

/** ProfilePage "Monk (Slack bot)" section status (D-13). */
export function useBotStatus() {
  return useQuery({
    queryKey: BOT_CONNECTION_KEY,
    queryFn: getBotStatus,
  });
}

/**
 * MKS-388 — is the caller's time logging paused for a missing Monka
 * connection?
 *
 * `staleTime` is 30 s and the query refetches on window focus, which keeps the
 * banner well inside the ticket's "no cache delay over one minute": the usual
 * way back from connecting is to re-focus the tab, which refetches at once.
 */
export function useTimeLoggingStatus() {
  return useQuery({
    queryKey: ['bot-time-logging-status'],
    queryFn: getTimeLoggingStatus,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/** ConnectPage validity probe for `:nonce` — never throws on invalid/expired (D-03). */
export function useNonceValidity(nonce: string) {
  return useQuery({
    queryKey: ['bot-connect-nonce', nonce],
    queryFn: () => getNonceValidity(nonce),
    enabled: Boolean(nonce),
    retry: false,
  });
}

export function useConnectBot() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: ConnectInput) => connectBot(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOT_CONNECTION_KEY });
      // Connecting mints a new Monk PAT (Phase 71) — keep that list fresh too.
      queryClient.invalidateQueries({ queryKey: PERSONAL_API_TOKENS_KEY });
    },
    onError: (err) => onError(err, "Couldn't connect Monk"),
  });
}

/** Disconnect revokes the Monk PAT + deletes the stored Claude token (D-15). */
export function useDisconnectBot() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: () => disconnectBot(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOT_CONNECTION_KEY });
      queryClient.invalidateQueries({ queryKey: PERSONAL_API_TOKENS_KEY });
    },
    onError: (err) => onError(err, "Couldn't disconnect Monk"),
  });
}

export function useRotateClaudeToken() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: RotateInput) => rotateClaudeToken(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOT_CONNECTION_KEY });
    },
    onError: (err) => onError(err, "Couldn't rotate Claude token"),
  });
}

/**
 * quick-260817-hpp — nonce-free connect/replace mutation backing the
 * Profile page's paste-a-token form (both the never-connected "Connect" flow
 * and the already-connected "Replace token" flow). A create/revive on the
 * backend mints a fresh Monk PAT, so this invalidates BOTH the bot
 * connection status and the Personal API Tokens list — mirrors
 * `useConnectBot`.
 */
export function useSelfConnectBot() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: { claudeToken: string }) => selfConnectBot(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOT_CONNECTION_KEY });
      queryClient.invalidateQueries({ queryKey: PERSONAL_API_TOKENS_KEY });
    },
    onError: (err) => onError(err, "Couldn't connect Monk"),
  });
}

/**
 * Model picker mutation (Phase 77, Plan 77-04) — persists the selected Monka
 * model (or `null` for "default") via `PATCH /api/bot/model`, then
 * invalidates the status query so the picker reflects the saved value.
 */
export function useUpdateBotModel() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (model: string | null) => updateBotModel(model),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOT_CONNECTION_KEY });
    },
    onError: (err) => onError(err, "Couldn't update model"),
  });
}
